// ============================================================
// LIVE OPERATIONAL TWIN — Fase 10.3
// Visualização em tempo real: zonas, equipamentos, riscos
// ============================================================
import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTwinStore, selectCriticalZones, selectCriticalEquipment, selectActiveRisks } from '@/store/twinStore'
import {
  TwinZone, EquipmentStatus, SpatialRiskCard, RiskBadge, TwinStatusBar,
} from '@/design-system/twin'
import { SURFACE, TEXT } from '@/design-system/tokens'
import type { ObraZone } from '@/types/twin'

type TwinTab = 'zones' | 'equipment' | 'risks'

export function LiveOperationalTwin() {
  const {
    twinState, overallRiskScore, correlations, lastUpdated,
    selectedZoneId, setSelectedZone,
  } = useTwinStore()

  const criticalZones = useTwinStore(selectCriticalZones)
  const criticalEquip = useTwinStore(selectCriticalEquipment)
  const activeRisks   = useTwinStore(selectActiveRisks)

  const [tab, setTab] = useState<TwinTab>('zones')

  if (!twinState) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: TEXT.muted }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🏗️</div>
        <div style={{ fontSize: 14 }}>Digital Twin não carregado</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>Aguardando dados da obra…</div>
      </div>
    )
  }

  const activeCorr = correlations.filter(c => c.is_active).length
  const zones      = twinState.zones

  // Group zones by risk level
  const zonesByRisk: Record<string, ObraZone[]> = {
    emergency: zones.filter(z => (z.risk_level as string) === 'emergency'),
    critical:  zones.filter(z => z.risk_level === 'critical'),
    high:      zones.filter(z => z.risk_level === 'high'),
    moderate:  zones.filter(z => z.risk_level === 'moderate'),
    low:       zones.filter(z => z.risk_level === 'low'),
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      {/* Status bar */}
      <TwinStatusBar
        riskScore={overallRiskScore}
        criticalZones={criticalZones.length}
        criticalEquip={criticalEquip.length}
        activeCorrelations={activeCorr}
        lastUpdated={lastUpdated}
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8 }}>
        {(['zones', 'equipment', 'risks'] as TwinTab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding:      '6px 14px',
              borderRadius: 6,
              border:       'none',
              background:   tab === t ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.05)',
              color:        tab === t ? '#3b82f6' : TEXT.muted,
              fontSize:     12,
              fontWeight:   tab === t ? 700 : 400,
              cursor:       'pointer',
            }}
          >
            {t === 'zones'     ? `Zonas (${zones.length})`         : ''}
            {t === 'equipment' ? `Equipamentos (${twinState.equipment.length})` : ''}
            {t === 'risks'     ? `Riscos (${activeRisks.length})`  : ''}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <AnimatePresence mode="wait">
          {tab === 'zones' && (
            <motion.div key="zones" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Criticals first */}
              {(['emergency', 'critical', 'high', 'moderate', 'low'] as const).map(level => {
                const zs = zonesByRisk[level]
                if (!zs || zs.length === 0) return null
                return (
                  <div key={level} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: TEXT.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                      {level.toUpperCase()} ({zs.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {zs.map(zone => (
                        <TwinZone
                          key={zone.id}
                          zone={zone}
                          selected={selectedZoneId === zone.id}
                          onClick={() => setSelectedZone(selectedZoneId === zone.id ? null : zone.id)}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
              {zones.length === 0 && (
                <EmptyState icon="🗺️" message="Nenhuma zona espacial cadastrada" />
              )}
            </motion.div>
          )}

          {tab === 'equipment' && (
            <motion.div key="equipment" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Critical/offline first */}
              {[...twinState.equipment]
                .sort((a, b) => {
                  const order = { offline: 4, critical: 3, maintenance: 2, degraded: 1, ok: 0 }
                  return (order[b.status as keyof typeof order] ?? 0) - (order[a.status as keyof typeof order] ?? 0)
                })
                .map(eq => <EquipmentStatus key={eq.id} equipment={eq} />)
              }
              {twinState.equipment.length === 0 && (
                <EmptyState icon="🏗️" message="Nenhum equipamento monitorado" />
              )}
            </motion.div>
          )}

          {tab === 'risks' && (
            <motion.div key="risks" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[...activeRisks]
                .sort((a, b) => b.score - a.score)
                .map(risk => <SpatialRiskCard key={risk.id} risk={risk} />)
              }
              {activeRisks.length === 0 && (
                <EmptyState icon="✅" message="Nenhum risco ativo identificado" sub="Operação dentro dos parâmetros" />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Selected zone detail */}
      <AnimatePresence>
        {selectedZoneId && (() => {
          const zone  = twinState.zones.find(z => z.id === selectedZoneId)
          const risks = twinState.zoneRiskMap?.get(selectedZoneId) ?? []
          if (!zone) return null
          return (
            <motion.div
              key="zone-detail"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              style={{
                padding:      14,
                background:   SURFACE.card,
                border:       '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: TEXT.primary }}>{zone.nome}</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <RiskBadge level={zone.risk_level} />
                  <button onClick={() => setSelectedZone(null)}
                    style={{ background: 'none', border: 'none', color: TEXT.muted, cursor: 'pointer', fontSize: 16 }}>✕</button>
                </div>
              </div>
              {risks.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {risks.slice(0, 3).map(r => (
                    <div key={r.id} style={{ fontSize: 12, color: TEXT.secondary }}>• {r.title}</div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: TEXT.muted }}>Sem riscos específicos nesta zona</div>
              )}
            </motion.div>
          )
        })()}
      </AnimatePresence>
    </div>
  )
}

function EmptyState({ icon, message, sub }: { icon: string; message: string; sub?: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 16px', color: TEXT.muted }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{message}</div>
      {sub && <div style={{ fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  )
}
